import React, {
  ChangeEvent, useEffect, useRef, useState,
} from 'react';
import {
  Button, Modal, Row,
} from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import {
  GetBlobOptions,
  ImageCropper,
  ImageCropperProps,
  useImageCropper,
} from '../../../../../../../dorian-shared/components/ImageCropper/ImageCropper';
import { imageFileToDataURL } from '../../../../../../../dorian-shared/components/ImageCropper/imageFileToDataURL';
import { useToggle } from '../../../../../../../dorian-shared/hooks/useToggle';
import { RewardBannerCoverFile } from '../../types/RewardBannerTypes';
import styles from './RewardBannerCoverInput.module.scss';

function createFileNameFromImageURL(imageURL: string) {
  return imageURL.split('/').slice(-1)[0] ?? 'cropped.jpg';
}

interface RewardBannerCoverInputProps {
  initialURL: string;
  onConfirm: (fileInputValue: RewardBannerCoverFile, imageURL: string) => void;
  error?: string;
  disabled?: boolean;
  imageCropperProps?: Partial<ImageCropperProps>;
  getBlobOptions?: GetBlobOptions;
  accept?: string;
}

export function RewardBannerCoverInput(props: RewardBannerCoverInputProps) {
  const {
    initialURL,
    onConfirm,
    error,
    disabled = false,
    imageCropperProps,
    getBlobOptions,
    accept,
  } = props;

  const [isCropperModalShown, showCropperModal, hideCropperModal] = useToggle();
  const [isModalConfirmDisabled, disableConfirmButton, enableConfirmButton] = useToggle();

  const [fileInputValue, setFileInputValue] = useState<RewardBannerCoverFile>();
  const [imageURLToCrop, setImageURLToCrop] = useState(initialURL);
  const lastFileNameRef = useRef(createFileNameFromImageURL(imageURLToCrop));

  const {
    handleCropperInitialized,
    getBlob,
  } = useImageCropper();

  const handleImageChange = (newFileInputValue: RewardBannerCoverFile) => {
    setFileInputValue(newFileInputValue);
    return imageFileToDataURL(newFileInputValue.file).then(setImageURLToCrop);
  };

  const handleEditClick = () => {
    setImageURLToCrop(initialURL);
    showCropperModal();
    disableConfirmButton();
  };

  const handleCropClick = () => getBlob(getBlobOptions).then((blob) => {
    if (blob) {
      handleImageChange({
        file: blob,
        fileName: lastFileNameRef.current,
      }).then(enableConfirmButton);
    }
  });

  const handleModalConfirmClick = () => {
    if (fileInputValue && imageURLToCrop) {
      onConfirm(fileInputValue, imageURLToCrop);
      hideCropperModal();
    }
  };

  const handleNewFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    const fileName = event.target.value;
    if (file) {
      lastFileNameRef.current = fileName;
      disableConfirmButton();
      handleImageChange({
        file,
        fileName,
      }).then(showCropperModal);
    }
  };

  useEffect(() => {
    setImageURLToCrop(initialURL);
  }, [initialURL]);

  return (
    <>
      <Row className="mb-3">
        {initialURL && (
          <Col className={styles.imageContainer}>
            <img
              alt="event cover"
              src={initialURL}
              className={styles.image}
            />
            {!disabled && (
              <Button
                className={styles.button}
                variant="primary"
                onClick={handleEditClick}
              >
                Edit
              </Button>
            )}
          </Col>
        )}
        <Form.Group as={Col}>
          <Form.Label>Cover</Form.Label>
          <Form.Control
            type="file"
            name="image"
            onChange={handleNewFileUpload}
            isInvalid={!!error}
            accept={accept}
            disabled={disabled}
          />
          <Form.Control.Feedback type="invalid">
            {error}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Modal
        size="lg"
        show={isCropperModalShown}
        onHide={hideCropperModal}
        centered
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Prepare image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <ImageCropper
              style={{ width: '100%' }}
              src={imageURLToCrop}
              onInitialized={handleCropperInitialized}
              {...imageCropperProps}
            />
          </Row>
          <Modal.Footer>
            <Button
              className={styles.button}
              variant="primary"
              onClick={handleCropClick}
            >
              Crop
            </Button>
            <Button
              disabled={isModalConfirmDisabled}
              className={styles.button}
              variant="primary"
              onClick={handleModalConfirmClick}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </>
  );
}
